import { JsonProperty, Serializable } from 'ts-jackson'

@Serializable()
export class BusinessAddress {
  @JsonProperty('addressLine') addressLine: string | null = null
  @JsonProperty('subDistrict') subDistrict: string | null = null
  @JsonProperty('district') district: string | null = null
  @JsonProperty('province') province: string | null = null
  @JsonProperty('zipcode') zipcode: string | null = null
  @JsonProperty('provinceId') provinceId: number | null = null
  @JsonProperty('districtId') districtId: number | null = null
  @JsonProperty('subDistrictId') subDistrictId: number | null = null
}

@Serializable()
export class BusinessDetail {
  @JsonProperty('businessName') businessName: string | null = null
  @JsonProperty('taxIdentificationNumber') taxIdentificationNumber: string | null = null
  @JsonProperty({ path: 'address', type: BusinessAddress }) address: BusinessAddress | null = null
}

@Serializable()
export class ContactInformation {
  @JsonProperty('officeNumber') officeNumber: string | null = null
  @JsonProperty('mobileNumber') mobileNumber: string | null = null
  @JsonProperty('faxNumber') faxNumber: string | null = null
}

@Serializable()
export class BusinessInformation {
  @JsonProperty('companyName') companyName: string | null = null
  @JsonProperty('businessType') businessType: string | null = null
  @JsonProperty('isVatRegistered') isVatRegistered: boolean | null = null
  @JsonProperty('vatRate') vatRate: number | null = null
}

/**
 * What happens when a member cancels a class after the cancellation deadline
 * (`schedule.startTime − schedule.cancelBefore`).
 *
 * - `BLOCK` — the cancel is refused outright (today's behaviour, made explicit).
 * - `FORFEIT` — the cancel goes through but nothing is given back.
 * - `STRIKE` — the cancel goes through WITH the refund, and the club records it
 *   as a strike (optionally counted by the auto-ban scheduler).
 */
export const CLASS_LATE_CANCEL_MODE = {
  BLOCK: 'BLOCK',
  FORFEIT: 'FORFEIT',
  STRIKE: 'STRIKE',
} as const

export type ClassLateCancelMode =
  (typeof CLASS_LATE_CANCEL_MODE)[keyof typeof CLASS_LATE_CANCEL_MODE]

/**
 * Club-wide late-cancel policy, stored inside `fitness.config` and edited
 * through PATCH /club-info like `classBookingQuotaPerMonth`.
 *
 * `countsAsAbsence` only means anything under STRIKE: BLOCK never produces a
 * late cancel, and FORFEIT already takes the session.
 */
@Serializable()
export class ClubLateCancelConfig {
  @JsonProperty('mode') mode: ClassLateCancelMode = CLASS_LATE_CANCEL_MODE.BLOCK
  @JsonProperty('countsAsAbsence') countsAsAbsence: boolean = false
}

@Serializable()
export class ClubInfoConfig {
  @JsonProperty('isCreditEnabled') isCreditEnabled: boolean = false
  @JsonProperty('isLoyaltyEnabled') isLoyaltyEnabled: boolean = false
  @JsonProperty('isESignatureEnabled') isESignatureEnabled: boolean = false
  @JsonProperty('is80mmReceipt') is80mmReceipt: boolean = false
  @JsonProperty('isAccessControlEnabled') isAccessControlEnabled: boolean = false
  @JsonProperty('isRfidCheckinEnabled') isRfidCheckinEnabled: boolean = false
  @JsonProperty('isHumanArchitectEnabled') isHumanArchitectEnabled: boolean = false
  @JsonProperty('isAiCoachingEnabled') isAiCoachingEnabled: boolean = false
  @JsonProperty('isLactateTestEnabled') isLactateTestEnabled: boolean = false
  @JsonProperty('isWorkoutBuilderEnabled') isWorkoutBuilderEnabled: boolean = false
  @JsonProperty('classBookingQuotaPerMonth') classBookingQuotaPerMonth: number = 5
  /** Hours before a PT session a member can still cancel it. 0 = until start. */
  @JsonProperty('ptCancelBeforeHours') ptCancelBeforeHours: number = 24
  /**
   * Nested ts-jackson models arrive as PLAIN objects under Vite, so read this
   * as data only — never add methods to `ClubLateCancelConfig`.
   */
  @JsonProperty({ path: 'lateCancel', type: ClubLateCancelConfig })
  lateCancel: ClubLateCancelConfig | null = null
}

/** The stored policy, or the backend's documented default when none is set. */
export function getLateCancelPolicy(config?: ClubInfoConfig | null): {
  mode: ClassLateCancelMode
  countsAsAbsence: boolean
} {
  return {
    mode: config?.lateCancel?.mode ?? CLASS_LATE_CANCEL_MODE.BLOCK,
    countsAsAbsence: config?.lateCancel?.countsAsAbsence ?? false,
  }
}

@Serializable()
export class UsageLimit {
  @JsonProperty('limit') limit: number = 0
  @JsonProperty('used') used: number = 0
  @JsonProperty('remaining') remaining: number = 0
}

@Serializable()
export class ClubInfo {
  @JsonProperty('imageUrl') imageUrl: string | null = null
  @JsonProperty({ path: 'businessInformation', type: BusinessInformation })
  businessInformation: BusinessInformation | null = null
  @JsonProperty({ path: 'contactInformation', type: ContactInformation })
  contactInformation: ContactInformation | null = null
  @JsonProperty({ path: 'businessDetail', type: BusinessDetail })
  businessDetail: BusinessDetail | null = null
  @JsonProperty({ path: 'config', type: ClubInfoConfig }) config: ClubInfoConfig | null = null
  @JsonProperty('isDownloadCsvReportAvailable') isDownloadCsvReportAvailable: boolean = false
}

import type { CardDesign } from '@/features/club-management/components/card-designer/types'

export interface MembershipCard {
  id: number
  name: string
  details: string | null
  logoUrl: string | null
  backgroundUrl: string | null
  design: CardDesign
  createdAt: string
  updatedAt: string
}

export interface UpdateMembershipCardPayload {
  name?: string
  details?: string
  logoUrl?: string | null
  design?: CardDesign
}

export interface UpdateClubInfoPayload {
  imageUrl?: string
  companyName?: string
  businessName?: string
  businessType?: string
  isVatRegistered?: boolean
  officeNumber?: string
  mobileNumber?: string
  faxNumber?: string
  taxIdentificationNumber?: string
  addressLine?: string
  provinceId?: number
  districtId?: number
  subDistrictId?: number
  zipcode?: string
  classBookingQuotaPerMonth?: number
  /** PATCH takes the WHOLE nested object — send both fields or neither. */
  lateCancel?: { mode: ClassLateCancelMode; countsAsAbsence: boolean }
  ptCancelBeforeHours?: number
}

export interface BankFee {
  id: number
  name: string
  feeRate: number
}

export interface UpdateBankFeePayload {
  feeRate: number
}

export interface FitnessBank {
  id: string
  name: string
  accountName: string
  accountNo: string
  bankBranchName: string | null
  branchId: number
  createdAt: string
  updatedAt: string
}

export interface CreateFitnessBankPayload {
  name: string
  accountName: string
  accountNo: string
  bankBranchName?: string
  branchId: number
}

export type UpdateFitnessBankPayload = Partial<Omit<CreateFitnessBankPayload, 'branchId'>> & {
  branchId?: number
}
